import { useCallback, useEffect, useRef, useState } from "react";
import { fetchNewsSidebar } from "services/posts.service";
import { NewsSidebar } from "types/Post";

/**
 * Sidebar data for the news page, loaded again whenever the feed filter changes.
 * It is a bonus next to the feed: a failed request only leaves the sidebar out.
 * Neither a filter change nor `reload` blanks what is on screen; the new answer replaces it,
 * so the layout does not jump.
 */
export function useNewsSidebar(companyId: string, filter: string) {
  // `filter` says which scope the lists belong to, so a failed request can drop lists of another scope.
  const [loaded, setLoaded] = useState<{ filter: string; sidebar: NewsSidebar } | null>(null);
  // Every request starts a new generation; an answer of an older one (another filter, a refresh) is dropped.
  const generation = useRef(0);

  const reload = useCallback(() => {
    generation.current += 1;
    const myGeneration = generation.current;

    fetchNewsSidebar(companyId, filter)
      .then((sidebar) => {
        if (myGeneration === generation.current) setLoaded({ filter, sidebar });
      })
      .catch((err) => {
        console.error("Error loading news sidebar:", err);
        if (myGeneration === generation.current) setLoaded((prev) => (prev?.filter === filter ? prev : null));
      });
  }, [companyId, filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { sidebar: loaded?.sidebar ?? null, reloadSidebar: reload };
}
